import { verifiesOwnerMessage } from "@mudwater/sdk";
import { getAddressEncoder, isAddress } from "@solana/kit";

/**
 * Sign-In-With-Solana, verified with WebCrypto rather than a library.
 *
 * The sibling of `glyph-token.ts`: that one verifies a JWT Privy issued, this
 * one verifies an Ed25519 signature over a message this server composed.
 *
 * The address line is a placeholder until a wallet claims the challenge,
 * because the challenge is issued before anyone has said who they are.
 *
 * Verification reads the fields out of the text that came back rather than
 * comparing it to the text we composed. That is the spec's own model, and it
 * is forced here: `solana:signIn` has the *wallet* build the message from
 * structured input, so a byte comparison would be comparing our rendering to
 * Phantom's and failing over a space. The fields are what carry the meaning —
 * our nonce, our domain, the claimed address — and a signature over text
 * holding all three cannot have been obtained anywhere else.
 */
export const ADDRESS_PLACEHOLDER = "{address}";

/** Shown by both routes: ours in the text we compose, and Phantom's in the
 * message it renders from the structured input. */
export const STATEMENT =
  "Sign in to Mudwater. This does not approve any transaction.";

export interface ChallengeInput {
  domain: string;
  uri: string;
  nonce: string;
  issuedAt: string;
  expirationTime: string;
}

/**
 * The ABNF the wallet displays, in the order the SIWS draft fixes.
 *
 * `Chain ID` is deliberately absent. Phantom recognises a SIWS-shaped message
 * handed to `signMessage` and validates it, and it refuses outright when the
 * chain named does not match the cluster the holder's wallet is on — which is
 * theirs to choose and not something this app can know. The field is optional
 * in the draft and binds nothing here: this sign-in records who the holder is
 * and authorizes no transaction, so the domain, the nonce and the expiry are
 * what have to be right. Naming a cluster bought a refusal and no safety.
 */
export function signInMessage(input: ChallengeInput): string {
  return [
    `${input.domain} wants you to sign in with your Solana account:`,
    ADDRESS_PLACEHOLDER,
    "",
    STATEMENT,
    "",
    `URI: ${input.uri}`,
    "Version: 1",
    `Nonce: ${input.nonce}`,
    `Issued At: ${input.issuedAt}`,
    `Expiration Time: ${input.expirationTime}`,
  ].join("\n");
}

export function messageForAddress(storedText: string, address: string): string {
  return storedText.replace(ADDRESS_PLACEHOLDER, address);
}

/** A `Key: value` line, read by prefix rather than by position. */
function field(lines: readonly string[], key: string): string | undefined {
  const line = lines.find((candidate) => candidate.startsWith(`${key}: `));
  return line?.slice(key.length + 2).trim();
}

/**
 * Whether the signed text is an answer to the challenge we issued.
 *
 * Three things have to hold, and none is redundant. The nonce is the only
 * unforgeable link back to a row this server created, and it is already spent
 * by the time this runs. The domain stops a signature collected by another
 * site being presented here. The address line has to name the same account
 * the signature is checked against, or a holder could be signed in as
 * somebody else's wallet.
 *
 * Anything beyond those — the statement's wording, the URI, the issue time —
 * is the wallet's to render and not ours to insist on.
 */
export function signedTextAnswers(input: {
  text: string;
  domain: string;
  nonce: string;
  address: string;
}): boolean {
  const lines = input.text.split("\n");
  return (
    lines[0] ===
      `${input.domain} wants you to sign in with your Solana account:` &&
    lines[1]?.trim() === input.address &&
    field(lines, "Nonce") === input.nonce
  );
}

/**
 * Whether `signature` is the holder of `address` signing a text that answers
 * our challenge.
 *
 * Both halves matter and neither is sufficient. A good signature over a
 * message that is not an answer to this challenge proves nothing about this
 * sign-in, and an answering text with a bad signature proves nothing about
 * who sent it.
 */
export function verifySignIn(input: SignInProof): Promise<boolean> {
  return Promise.resolve(signInVerifies(input));
}

interface SignInProof {
  address: string;
  domain: string;
  nonce: string;
  signedMessage: Uint8Array;
  signature: Uint8Array;
}

function signInVerifies(input: SignInProof): boolean {
  const { address, domain, nonce, signedMessage, signature } = input;
  if (!isAddress(address)) return false;
  // Ed25519 signatures are 64 bytes.
  if (signature.length !== 64) return false;
  const text = new TextDecoder().decode(signedMessage);
  if (!signedTextAnswers({ text, domain, nonce, address })) return false;

  try {
    // Plain, or the off-chain envelope a Ledger in Phantom signs instead.
    return verifiesOwnerMessage(
      signedMessage,
      signature,
      new Uint8Array(getAddressEncoder().encode(address))
    );
  } catch {
    // A malformed key is a failed verification, never a pass.
    return false;
  }
}
